/**
 * Unified user display name and initials utilities.
 *
 * Accepts any object with first_name/last_name/email (snake_case — UserData, profiles)
 * or firstName/lastName/email (camelCase).
 */

interface SnakeCaseUser {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

interface CamelCaseUser {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  email?: string | null;
}

type UserLike = SnakeCaseUser | CamelCaseUser;

function resolveNames(user: UserLike): { first: string | null; last: string | null; email: string | null } {
  if ("first_name" in user || "last_name" in user) {
    const u = user as SnakeCaseUser;
    return { first: u.first_name ?? null, last: u.last_name ?? null, email: u.email ?? null };
  }
  const u = user as CamelCaseUser;
  // If only fullName is available, split it
  if (u.fullName && !u.firstName && !u.lastName) {
    const parts = u.fullName.trim().split(/\s+/);
    return { first: parts[0] ?? null, last: parts.slice(1).join(" ") || null, email: u.email ?? null };
  }
  return { first: u.firstName ?? null, last: u.lastName ?? null, email: u.email ?? null };
}

/**
 * Returns a display name for a user.
 *
 * Priority: "First Last" > "First" > "Last" > email > fallback
 */
export function getDisplayName(user: UserLike | null | undefined, fallback = "Unknown"): string {
  if (!user) return fallback;
  const { first, last, email } = resolveNames(user);
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;
  return email || fallback;
}

/**
 * Returns 1-2 character initials for avatar fallbacks.
 *
 * Priority: "FL" (first+last initials) > first two chars of first name >
 *           first char of email > "?"
 */
export function getUserInitials(user: UserLike | null | undefined, fallback = "?"): string {
  if (!user) return fallback;
  const { first, last, email } = resolveNames(user);
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  if (email) return email[0].toUpperCase();
  return fallback;
}
